<!-- src/components/common/PwaInstallBanner.vue -->
<template>
  <transition name="slide-up">
    <div v-if="shouldShowBanner" class="pwa-install-banner">
      <div class="banner-inner">
        <div class="d-flex align-center ga-3">
          <div class="pwa-app-icon">
            <v-icon icon="mdi-play-box-multiple" color="primary" size="22" />
          </div>
          <div class="pwa-app-text">
            <div class="pwa-app-title">ติดตั้ง SKOPreview App</div>
            <div class="pwa-app-desc">
              {{ isIos ? 'แตะปุ่มแชร์แล้วเลือก "เพิ่มไปยังหน้าจอโฮม"' : 'ใช้งานแบบเต็มจอ เร็วขึ้น และเปิดไฟล์ได้สะดวก' }}
            </div>
          </div>
        </div>

        <div class="d-flex align-center ga-2 flex-shrink-0">
          <v-btn
            v-if="isInstallable"
            color="primary"
            variant="flat"
            size="small"
            rounded="lg"
            prepend-icon="mdi-download"
            class="install-btn text-none"
            @click="handleInstall"
          >
            ติดตั้ง
          </v-btn>

          <v-btn
            v-else-if="isIos"
            color="primary"
            variant="tonal"
            size="small"
            rounded="lg"
            class="text-none"
            @click="showIosInstruction = !showIosInstruction"
          >
            วิธีติดตั้ง
          </v-btn>

          <v-btn
            icon="mdi-close"
            variant="text"
            size="x-small"
            color="medium-emphasis"
            @click="dismissBanner"
          />
        </div>
      </div>

      <!-- iOS Step-by-Step Tooltip/Drawer -->
      <v-expand-transition>
        <div v-if="showIosInstruction" class="ios-instruction-box mt-2 pa-3">
          <div class="d-flex align-center ga-2 text-caption mb-1">
            <v-icon icon="mdi-apple" size="16" color="primary" />
            <span class="font-weight-bold text-slate-100">ขั้นตอนการติดตั้งบน iPhone / iPad:</span>
          </div>
          <ol class="text-caption text-medium-emphasis pl-4 mb-0">
            <li>แตะปุ่ม <strong>แชร์ (Share)</strong> <v-icon icon="mdi-export-variant" size="14" /> ที่แถบด้านล่างของ Safari</li>
            <li>เลื่อนลงมาแล้วเลือก <strong>"เพิ่มไปยังหน้าจอโฮม (Add to Home Screen)"</strong> <v-icon icon="mdi-plus-box" size="14" /></li>
            <li>แตะ <strong>"เพิ่ม (Add)"</strong> ที่มุมขวาบน</li>
          </ol>
        </div>
      </v-expand-transition>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useDeviceEnvironment } from '@/composables/useDeviceEnvironment'

const { isPwa, isMobile, isInstallable, promptPwaInstall } = useDeviceEnvironment()

const dismissed = ref(false)
const showIosInstruction = ref(false)
const isIos = ref(false)

onMounted(() => {
  if (typeof window !== 'undefined') {
    isIos.value = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    const saved = sessionStorage.getItem('pwa_banner_dismissed')
    if (saved === 'true') {
      dismissed.value = true
    }
  }
})

const shouldShowBanner = computed(() => {
  if (isPwa.value || dismissed.value) return false
  return isInstallable.value || (isMobile.value && isIos.value)
})

async function handleInstall() {
  const installed = await promptPwaInstall()
  if (installed) {
    dismissBanner()
  }
}

function dismissBanner() {
  dismissed.value = true
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('pwa_banner_dismissed', 'true')
  }
}
</script>

<style scoped>
.pwa-install-banner {
  position: fixed;
  bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  left: 12px;
  right: 12px;
  max-width: 480px;
  margin: 0 auto;
  z-index: 1000;
  background: rgba(23, 26, 35, 0.95);
  border: 1px solid rgba(59, 130, 246, 0.35);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.7);
  border-radius: 14px;
  padding: 10px 14px;
  backdrop-filter: blur(14px);
  user-select: none;
}

.banner-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.pwa-app-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(59, 130, 246, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pwa-app-title {
  font-size: 13px;
  font-weight: 700;
  color: #f1f5f9;
}

.pwa-app-desc {
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.3;
}

.install-btn {
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
}

.ios-instruction-box {
  background: rgba(15, 23, 42, 0.7);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
